import { memo } from 'react'
import { useIntl } from 'react-intl'
import { type Moment } from 'moment'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import AspectRatio from 'components/utils/AspectRatio'
import styled from 'styled-components'
import TextButton from 'LuxKit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Image from 'components/Common/Image/Image'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'

const Root = styled(Group)`
  width: 75vw;
  position: relative;

  ${mediaQueryUp.tablet} {
    width: 40vw;
  }

  ${mediaQueryUp.desktop} {
    width: unset;
  }
  
  body.${REBRAND_PALETTE_CLASS} & {
    border-radius: var(--border-radius-m);
    overflow: hidden;
  }
`

const NestedLink = styled(ClickableLink)`
  position: absolute;
  inset: 0;
  z-index: 0;
`

const ImageContainer = styled(AspectRatio)`
  body.${REBRAND_PALETTE_CLASS} & {
    border-radius: var(--border-radius-m);
    overflow: hidden;
  }
`

// Base properties shared across all props
interface BaseProps {
  title: string
  date?: Moment
  description: string
  lineClamp?: number
}

// URL or Slug requirement (Mutually Exclusive)
type SlugOrUrl =
  | { slug: string, url?: never }
  | { slug?: never, url: string }

// Image requirement (Mutually Exclusive)
type ImageOrImageId =
  | { imageId: string, image?: never }
  | { imageId?: never, image: App.Image }

// Final Props Type
type Props = BaseProps & SlugOrUrl & ImageOrImageId

function BlogCard(props: Props) {
  const { lineClamp = 3, title, date, description, slug, imageId, image, url } = props
  const intl = useIntl()

  return (
    <Root direction="vertical" verticalAlign="space-between" gap={12}>
      <VerticalSpacer gap={12}>
        <ImageContainer ratio="16:9">
          <Image
            alt={title}
            aspectRatio="16:9"
            id={imageId}
            image={image}
            fit="center"
            dpr={2}
            width="375"
            loading="lazy"
          />
        </ImageContainer>
        <VerticalSpacer gap={4}>
          <Heading variant="heading4" lineClamp={2}>{title}</Heading>
          {date && <BodyText variant="medium">{intlDateFormatter(date, INTL_DMY_CASUAL_SHORT_FORMAT, intl.locale)}</BodyText>}
        </VerticalSpacer>
        <BodyText variant="medium" lineClamp={lineClamp}>{description}</BodyText>
      </VerticalSpacer>
      <CSSBreakpoint min="tablet">
        <Group direction="horizontal" horizontalAlign="end">
          <TextButton kind="primary">
            {intl.formatMessage({
              id: 'Y_szdIQBDbK_99YjVInF',
              defaultMessage: 'View article',
              description: 'Action button label to view a blog article on the homepage',
            })}
          </TextButton>
        </Group>
      </CSSBreakpoint>
      <NestedLink target="_blank" href={url || `https://luxuryescapes.com/inspiration/${slug}`} />
    </Root>
  )
}

export default memo(BlogCard)
